// ═══════════════════════════════════════════════════════════════════════════
// Attribute-definition AI translators (CREATE_ATTDEF / UPDATE_ATTDEF /
// RENAME_ATTDEF_TAG / REMOVE_ATTDEF / REORDER_ATTDEFS).
// ═══════════════════════════════════════════════════════════════════════════

import type { EditorCommand } from "@dxf-ai-editor/contracts";
import { coerceColor } from "@dxf-ai-editor/drawing-model";

import type {
  CreateAttdefAiCommand,
  RemoveAttdefAiCommand,
  RenameAttdefTagAiCommand,
  ReorderAttdefsAiCommand,
  UpdateAttdefAiCommand,
} from "./blockSchemas";
import { AI_COMMAND_SOURCE } from "./blockDispatcherShared";

// ─── CREATE_ATTDEF ────────────────────────────────────────────────
//
// Must run inside a BEDIT session. We wrap the add command in
// `space/enter-block-edit` / `space/exit-block-edit action=save` so the
// AI can create ATTDEFs from a fresh session. When the AI already
// opened BEDIT via CREATE_DYNAMIC_BLOCK / explicit command the second
// enter is a no-op (reducer tolerates re-entry — same block name).
export function translateCreateAttdef(
  command: CreateAttdefAiCommand,
): EditorCommand[] {
  return [
    {
      type: "space/enter-block-edit",
      source: AI_COMMAND_SOURCE,
      blockName: command.blockName,
      createIfMissing: false,
    },
    {
      type: "geometry/create-attdef",
      source: AI_COMMAND_SOURCE,
      blockName: command.blockName,
      attdef: {
        tag: command.tag,
        prompt: command.prompt,
        defaultValue: command.defaultValue,
        insertionPoint: {
          x: command.insertionPoint.x,
          y: command.insertionPoint.y,
        },
        height: command.height,
        rotation: command.rotation,
        widthFactor: command.widthFactor,
        obliqueAngle: command.obliqueAngle,
        styleName: command.styleName,
        hJustification: command.hJustification,
        vJustification: command.vJustification,
        flags: {
          invisible: command.flags.invisible,
          constant: command.flags.constant,
          verify: command.flags.verify,
          preset: command.flags.preset,
          lockPosition: command.flags.lockPosition,
          multiline: command.flags.multiline,
        },
        backgroundFill: false,
        fillBoxScale: 1.5,
      },
      layer: command.layer,
      // CECOLOR (EPIC-COLOR-UNION round-6 FINDING B) — the create-attdef contract
      // `color` is the typed EntityColor union (an ATTDEF is a drawn entity). Emit
      // it ONLY when the AI request explicitly named a color: coerce the legacy
      // STRING (ACI / ByLayer / hex) into the union and honour it as an override.
      // When the AI omitted color, leave the command's `color` UNDEFINED so the
      // dispatch chokepoint stamps the live CECOLOR (mirroring how the other AI
      // create paths leave color unstamped) — previously the forced ByLayer
      // default materialized here defeated the central stamp.
      ...(command.color !== undefined
        ? { color: coerceColor(command.color) }
        : {}),
      linetype: command.linetype,
      lineweight: command.lineweight,
    },
    {
      type: "space/exit-block-edit",
      source: AI_COMMAND_SOURCE,
      action: "save",
    },
  ];
}

// ─── UPDATE_ATTDEF / RENAME_ATTDEF_TAG / REMOVE_ATTDEF / REORDER_ATTDEFS
export function translateUpdateAttdef(
  command: UpdateAttdefAiCommand,
): EditorCommand[] {
  return [
    {
      type: "geometry/update-attdef",
      source: AI_COMMAND_SOURCE,
      blockName: command.blockName,
      attdefId: command.attdefId,
      updates: command.updates,
    },
  ];
}

export function translateRenameAttdefTag(
  command: RenameAttdefTagAiCommand,
): EditorCommand[] {
  return [
    {
      type: "geometry/rename-attdef-tag",
      source: AI_COMMAND_SOURCE,
      blockName: command.blockName,
      attdefId: command.attdefId,
      newTag: command.newTag,
    },
  ];
}

export function translateRemoveAttdef(
  command: RemoveAttdefAiCommand,
): EditorCommand[] {
  return [
    {
      type: "geometry/remove-attdef",
      source: AI_COMMAND_SOURCE,
      blockName: command.blockName,
      attdefId: command.attdefId,
      removeFromInserts: command.removeFromInserts,
    },
  ];
}

export function translateReorderAttdefs(
  command: ReorderAttdefsAiCommand,
): EditorCommand[] {
  return [
    {
      type: "geometry/reorder-attdefs",
      source: AI_COMMAND_SOURCE,
      blockName: command.blockName,
      attdefIds: command.attdefIds,
    },
  ];
}
